import React,{ useState, useEffect, useRef, useMemo } from 'react';
import { downloadCsv } from '../common/sharedFunctions';
import MaterialTable from "material-table";
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import moment from 'moment';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PersonIcon from '@mui/icons-material/Person';

export default function Users() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const {
    addUser,
    editUser,
    deleteUser,
    checkUserExists,
    fetchUsers
  } = api;
  const [data, setData] = useState([]);
  const users = useSelector(state => state.usersdata.users);
  const dispatch = useDispatch();
  const loaded = useRef(false);

  useEffect(()=>{
    // Real-time listener - updates instantly when data changes
    dispatch(fetchUsers());
  },[dispatch,fetchUsers]);

  useEffect(()=>{
    if(users){
      setData(users.filter(user => user.usertype ==='fleetadmin'));
    }else{
      setData([]);
    }
    loaded.current = true;
  },[users]);

  // Uber-style columns with status dots
  const columns = [
    { title: t('fleetadmin'), field: 'firstName',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {rowData.profile_image ? (
            <img alt='Profile' src={rowData.profile_image} style={{width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid #F3F4F6'}}/>
          ) : (
            <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PersonIcon sx={{ color: '#9CA3AF', fontSize: 22 }} />
            </Box>
          )}
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
              {rowData.firstName} {rowData.lastName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
              {settings.AllowCriticalEditsAdmin ? rowData.mobile : "Hidden"}
            </Typography>
          </Box>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('email'), field: 'email', editable:'onAdd',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {settings.AllowCriticalEditsAdmin ? rowData.email : "Hidden"}
        </Typography>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('createdAt'), field: 'createdAt', editable:'never', defaultSort:'desc',
      render: rowData => (
        <Typography sx={{ fontSize: 14, color: '#374151' }}>
          {rowData.createdAt ? moment(rowData.createdAt).format('lll') : '-'}
        </Typography>
      ),
      exportTransformer: (rowData) => new Date(rowData.createdAt).toLocaleDateString() + ' '+ new Date(rowData.createdAt).toLocaleTimeString(),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('account_approve'), field: 'approved', type:'boolean', initialEditValue: true,
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FiberManualRecordIcon sx={{ fontSize: 10, color: rowData.approved ? '#10B981' : '#F59E0B' }} />
          <Typography sx={{ fontSize: 14, color: '#374151' }}>
            {rowData.approved ? (t('approved') || 'Approved') : (t('pending') || 'Pending')}
          </Typography>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
  ];

  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterApproved, setFilterApproved] = useState('all');
  const [approvalAnchor, setApprovalAnchor] = useState(null);

  // Filtered data
  const filteredData = useMemo(() => {
    let result = data;
    if (filterApproved !== 'all') {
      result = result.filter(d => filterApproved === 'approved' ? d.approved : !d.approved);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.firstName && d.firstName.toLowerCase().includes(query)) ||
        (d.lastName && d.lastName.toLowerCase().includes(query)) ||
        (d.email && d.email.toLowerCase().includes(query)) ||
        (d.mobile && d.mobile.toLowerCase().includes(query))
      );
    }
    return result;
  }, [data, filterApproved, searchQuery]);

  const clearFilters = () => {
    setFilterApproved('all');
    setSearchQuery('');
  };

  const getApprovalLabel = () => {
    if (filterApproved === 'all') return t('approval') || 'Approval';
    return filterApproved === 'approved' ? t('approved') || 'Approved' : t('pending') || 'Pending';
  };

  return (
    !loaded.current? <CircularLoading/>:
    <div>
      {/* Uber-Style Filter Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          alignItems: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <TextField
          size="small"
          placeholder={t('search') || 'Search fleet admins...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              borderRadius: 50,
              bgcolor: '#F7F7F7',
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: '#EFEFEF' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ width: 1, height: 24, bgcolor: '#E5E7EB', mx: 1 }} />
        <Chip
          label={t('all') || 'All'}
          onClick={clearFilters}
          sx={{
            bgcolor: filterApproved === 'all' ? '#111827' : '#F7F7F7',
            color: filterApproved === 'all' ? '#FFFFFF' : '#374151',
            fontWeight: 500,
            fontSize: 13,
            height: 36,
            borderRadius: 50,
            px: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: filterApproved === 'all' ? '#000000' : '#EFEFEF' },
          }}
        />
        <Chip
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {filterApproved !== 'all' && (
                <FiberManualRecordIcon sx={{ fontSize: 8, color: filterApproved === 'approved' ? '#10B981' : '#F59E0B' }} />
              )}
              {getApprovalLabel()}
              <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
            </Box>
          }
          onClick={(e) => setApprovalAnchor(e.currentTarget)}
          sx={{
            bgcolor: filterApproved !== 'all' ? '#111827' : '#F7F7F7',
            color: filterApproved !== 'all' ? '#FFFFFF' : '#374151',
            fontWeight: 500,
            fontSize: 13,
            height: 36,
            borderRadius: 50,
            px: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: filterApproved !== 'all' ? '#000000' : '#EFEFEF' },
          }}
        />
        <Menu
          anchorEl={approvalAnchor}
          open={Boolean(approvalAnchor)}
          onClose={() => setApprovalAnchor(null)}
          PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 150, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
        >
          <MenuItem onClick={() => { setFilterApproved('all'); setApprovalAnchor(null); }}>
            <Typography sx={{ fontSize: 14 }}>{t('all') || 'All'}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setFilterApproved('approved'); setApprovalAnchor(null); }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#10B981', mr: 1 }} />
            <Typography sx={{ fontSize: 14 }}>{t('approved') || 'Approved'}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setFilterApproved('pending'); setApprovalAnchor(null); }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#F59E0B', mr: 1 }} />
            <Typography sx={{ fontSize: 14 }}>{t('pending') || 'Pending'}</Typography>
          </MenuItem>
        </Menu>
        <Typography sx={{ ml: 'auto', fontSize: 13, color: '#6B7280' }}>
          {filteredData.length} {t('fleetadmins') || 'fleet admins'}
        </Typography>
      </Box>

    <MaterialTable
      icons={TableIcons}
      title={null}
      columns={columns}
      style={{
        direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
        borderRadius: 12,
        boxShadow: 'none',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
      }}
      data={filteredData}
      onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
      options={{
        ...tableOptions,
        search: false,
        toolbar: false,
        showTitle: false,
        exportCsv: (columns, data) => {
          let hArray = [];
          const headerRow = columns.map(col => {
            if (typeof col.title === 'object') {
              return col.title.props.text;
            }
            hArray.push(col.field);
            return col.title;
          });
          const dataRows = data.map(({ tableData, ...row }) => {
            row.createdAt = new Date(row.createdAt).toLocaleDateString() + ' '+ new Date(row.createdAt).toLocaleTimeString()
            let dArr = [];
            for(let i=0;i< hArray.length; i++) {
              dArr.push(row[hArray[i]]);
            }
            return Object.values(dArr);
          })
          const { exportDelimiter } = ",";
          const delimiter = exportDelimiter ? exportDelimiter : ",";
          const csvContent = [headerRow, ...dataRows].map(e => e.join(delimiter)).join("\n");
          const csvFileName = 'download.csv';
          downloadCsv(csvContent, csvFileName);
        },
        exportButton: {
          csv: settings.AllowCriticalEditsAdmin,
          pdf: false,
        },
        maxColumnSort: "all_columns",
        rowStyle: rowData => ({
          backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
        }),
      }}
      localization={{body:{
        addTooltip: (t('add')),
        deleteTooltip: (t('delete')),
        editTooltip: (t('edit')),
        emptyDataSourceMessage: (
          (t('blank_message'))
      ),
      editRow: { 
        deleteText: (t('delete_message')),
        cancelTooltip: (t('cancel')),
        saveTooltip: (t('save')) 
          }, 
        },
        toolbar: {
          searchPlaceholder: (t('search')),
          exportTitle: (t('export')),
        },
        header: {
          actions: (t('actions')) 
      },
      pagination: {
        labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
       // labelRowsSelect: (t('rows')),
        firstTooltip: (t('first_page_tooltip')),
        previousTooltip: (t('previous_page_tooltip')),
        nextTooltip: (t('next_page_tooltip')),
        lastTooltip: (t('last_page_tooltip'))
      },
      }}
      editable={{
        onRowAdd: newData =>
        new Promise((resolve,reject) => {
          setTimeout(() => {
            checkUserExists(newData).then((res) => {
              if (res.users && res.users.length > 0) {
                alert(t('user_exists'));
                reject();
              }
              else if(!(newData && newData.firstName)){
                alert(t('proper_input_name'));
                reject();
              }
              else if(res.error){
                alert(t('email_or_mobile_issue'));
                reject();
              }
              else{
                newData['usertype'] = 'fleetadmin';
                newData['createdAt'] = new Date().getTime();
                delete newData.tableData;
                dispatch(addUser(newData));
                // Real-time listener will auto-update
                resolve();
              }
            });
          }, 600);
        }),
        onRowUpdate: (newData, oldData) =>
          new Promise((resolve,reject) => {
            setTimeout(() => {
              if(!(newData && newData.firstName)){
                alert(t('proper_input_name'));
                reject();
              }else {
                resolve();
                if(newData !== oldData){
                  delete newData.tableData;
                  dispatch(editUser(oldData.id,newData));
                  // Real-time listener will auto-update
                }
              }
            }, 600);
          }),
        onRowDelete: oldData =>
          settings.AllowCriticalEditsAdmin?
          new Promise(resolve => {
            setTimeout(() => {
              resolve();
              dispatch(deleteUser(oldData.id));
              // Real-time listener will auto-update
            }, 600);
          })
          :
          new Promise(resolve => {
            setTimeout(() => {
              resolve();
              alert(t('demo_mode'));
            }, 600);
          })
          ,
      }}
    />
    </div>
  );
}
